import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Loader2, Eye, Target, CheckCircle2 } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";

/**
 * Reusable Vimeo / YouTube / direct-mp4 fetch panel.
 * Used on the Studio landing page, Dashboard, and the editor's "+ Add Video" modal.
 *
 * Props:
 *   onComplete(videoId)  – called when fetch finishes & video is "ready"
 *   compact (bool)       – hide event-name/date fields for a tighter footprint
 *   defaultCategory      – initial category (default "barrel_racing")
 */
export default function UrlFetchPanel({ onComplete, compact = false, defaultCategory = "barrel_racing" }) {
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [eventName, setEventName] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [fetching, setFetching] = useState(false);
  const [progress, setProgress] = useState(null);
  const [watchMode, setWatchMode] = useState(false);
  const [autoIdentify, setAutoIdentify] = useState(true);
  const [eligibleRiders, setEligibleRiders] = useState([]);
  const [trackRiders, setTrackRiders] = useState(new Set());
  const isVimeo = /vimeo\.com\/(?:video\/)?\d+/.test(url);

  useEffect(() => {
    if (!watchMode || eligibleRiders.length > 0) return;
    api.get("/vimeo-watch/eligible-riders")
      .then((r) => setEligibleRiders(r.data.riders || []))
      .catch(() => {});
  }, [watchMode, eligibleRiders.length]);

  const toggleRider = (name) => {
    setTrackRiders((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const startWatch = async () => {
    setFetching(true);
    try {
      const r = await api.post("/vimeo-watch", {
        urls: [url.trim()],
        category,
        event_name: eventName.trim(),
        event_date: eventDate,
        auto_ai_suggest: true,
        auto_identify_riders: autoIdentify,
        track_riders: Array.from(trackRiders),
      });
      if (r.data.added?.length) {
        const trackN = trackRiders.size;
        toast.success(
          trackN > 0
            ? `Added to watchlist — auto-download + AI Suggest + track ${trackN} rider${trackN === 1 ? "" : "s"}`
            : "Added to Vimeo watchlist — we'll auto-download when it's ready",
          { duration: 6000 },
        );
        setUrl("");
      } else if (r.data.skipped?.length) {
        toast.error("Skipped: " + r.data.skipped[0].reason);
      }
    } catch (err) {
      toast.error("Watchlist add failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setFetching(false);
    }
  };

  const start = async () => {
    if (!url.trim()) return;
    if (watchMode) {
      if (!isVimeo) {
        toast.error("Watchlist only supports Vimeo URLs. Uncheck 'Watch mode' for other sources.");
        return;
      }
      return startWatch();
    }
    setFetching(true);
    setProgress({ pct: 0, stage: "starting", size: "", eta: "" });
    try {
      const res = await api.post("/videos/fetch-url", {
        url: url.trim(),
        category,
        event_name: eventName.trim(),
        event_date: eventDate,
      });
      const id = res.data.video_id;
      const deadline = Date.now() + 60 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const v = await api.get(`/videos/${id}`);
          const d = v.data;
          setProgress({
            pct: d.fetch_progress || 0,
            stage: d.fetch_stage || d.status || "downloading",
            size: d.fetch_size || "",
            eta: d.fetch_eta || "",
          });
          if (d.status === "ready") {
            toast.success("Video ready");
            onComplete && onComplete(id);
            return;
          }
          if (d.status === "failed") {
            const detail = d.notes || "Check the URL and that the video is public / downloadable.";
            toast.error("Fetch failed: " + detail, { duration: 12000 });
            return;
          }
        } catch {
          toast.error("Fetch failed — lost connection to backend");
          return;
        }
      }
    } catch (err) {
      toast.error("Fetch failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setFetching(false);
      setProgress(null);
      setUrl("");
    }
  };

  return (
    <div data-testid="url-fetch-panel">
      {!compact && (
        <>
          <div className="label-tiny mb-2">Discipline</div>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="url-fetch-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider border transition-colors ${
                  category === c.id
                    ? "bg-orange-700 border-orange-600 text-white"
                    : "border-stone-700 text-stone-400 hover:border-stone-500"
                }`}
                data-testid={`url-fetch-cat-${c.id}`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="label-tiny block mb-1">Event Name (optional)</label>
              <input
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                placeholder="Olds Open Jackpot"
                className="field-input"
                data-testid="url-fetch-event-name"
              />
            </div>
            <div>
              <label className="label-tiny block mb-1">Event Date (optional)</label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                className="field-input"
                data-testid="url-fetch-event-date"
              />
            </div>
          </div>
        </>
      )}

      <div className="flex gap-2" data-testid="url-fetch-row">
        <div className="relative flex-1">
          <Link2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Vimeo / YouTube / direct mp4 URL"
            className="field-input !pl-9"
            disabled={fetching}
            data-testid="url-fetch-input"
          />
        </div>
        <button
          type="button"
          onClick={start}
          disabled={fetching || !url.trim()}
          className="btn-primary"
          data-testid="url-fetch-btn"
        >
          {fetching ? <Loader2 size={14} className="animate-spin inline" /> : watchMode ? "Watch" : "Fetch"}
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-[10px] text-stone-500">
          Public Vimeo, YouTube, or any direct video URL. For private Vimeo videos, use file upload.
        </div>
        <label
          className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest cursor-pointer transition-colors ${
            watchMode ? "text-orange-400" : "text-stone-500 hover:text-stone-300"
          }`}
          title="Add to a watchlist — we'll auto-download the moment Vimeo finishes transcoding, then run AI Suggest Cuts."
          data-testid="watch-mode-toggle"
        >
          <input
            type="checkbox"
            checked={watchMode}
            onChange={(e) => setWatchMode(e.target.checked)}
            className="accent-orange-500"
          />
          <Eye size={11} /> Watch mode (auto-download when ready)
        </label>
      </div>

      {watchMode && (
        <div className="mt-3 border border-stone-800 bg-stone-950/60 p-3" data-testid="watch-track-riders">
          <label
            className="inline-flex items-center gap-2 text-[11px] text-stone-300 cursor-pointer mb-3 select-none"
            title="After download, transcribe announcer audio and fill blank rider names on cuts."
            data-testid="watch-auto-identify-toggle"
          >
            <input
              type="checkbox"
              checked={autoIdentify}
              onChange={(e) => setAutoIdentify(e.target.checked)}
              className="accent-orange-500"
            />
            Auto-identify riders from announcer audio
          </label>
          <div className="flex items-center gap-2 mb-2">
            <Target size={11} className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-widest text-stone-300">
              Auto-track riders <span className="text-stone-500 normal-case">(optional — uses each rider&apos;s saved reference frames)</span>
            </span>
          </div>
          {eligibleRiders.length === 0 ? (
            <div className="text-[10px] text-stone-500 italic">
              No riders have saved reference frames yet. Save frames from a rider profile to enable auto-tracking.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-1.5">
                {eligibleRiders.map((r) => {
                  const on = trackRiders.has(r.name);
                  return (
                    <button
                      key={r.name}
                      type="button"
                      onClick={() => toggleRider(r.name)}
                      className={`px-2 py-1 text-[11px] border transition-colors inline-flex items-center gap-1 ${
                        on
                          ? "bg-orange-700 border-orange-600 text-white"
                          : "border-stone-700 text-stone-400 hover:border-stone-500"
                      }`}
                      data-testid={`watch-track-rider-${r.name.replace(/\s+/g, "-")}`}
                      title={`${r.frame_count} reference frame${r.frame_count === 1 ? "" : "s"} in library`}
                    >
                      {on && <CheckCircle2 size={10} />}
                      {r.name}
                      <span className="font-mono-tc text-[9px] opacity-70">{r.frame_count}</span>
                    </button>
                  );
                })}
              </div>
              {trackRiders.size > 0 && (
                <div className="text-[10px] text-orange-400/90 mt-2">
                  {trackRiders.size} rider{trackRiders.size === 1 ? "" : "s"} will be auto-tracked once the video downloads.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {progress && (
        <div className="mt-4 border border-orange-700/40 bg-stone-900/50 px-4 py-3" data-testid="url-fetch-progress">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="text-orange-400 uppercase tracking-widest font-bold">
              {progress.stage === "downloading" && "Downloading"}
              {progress.stage === "merging" && "Merging streams"}
              {progress.stage === "uploading" && "Uploading to S3"}
              {progress.stage === "starting" && "Starting…"}
            </span>
            <span className="font-mono-tc text-stone-300">{progress.pct}%</span>
          </div>
          <div className="h-1.5 bg-stone-900 overflow-hidden">
            <div className="h-full bg-orange-500 transition-all" style={{ width: `${progress.pct}%` }} />
          </div>
          <div className="flex items-center justify-between text-[10px] font-mono-tc text-stone-500 mt-2">
            <span>{progress.size}</span>
            <span>{progress.eta && `ETA ${progress.eta}`}</span>
          </div>
        </div>
      )}
    </div>
  );
}
